import React, { useState, useRef, useEffect, useMemo } from 'react'
import { ChevronDown, Folder, RefreshCw, AlertCircle, Search, X } from 'lucide-react'
import { Project } from '../../types'

interface ProjectSelectorProps {
  projects: Project[]
  selectedProject: Project | null
  recentFavoriteProjects: Project[]
  onSelectProject: (project: Project | null) => void
  onReloadProjects?: () => Promise<void>
  disabled?: boolean
  className?: string
}

const ProjectSelector: React.FC<ProjectSelectorProps> = ({
  projects,
  selectedProject,
  recentFavoriteProjects,
  onSelectProject,
  onReloadProjects,
  disabled = false,
  className = ''
}) => {
  const [isReloading, setIsReloading] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [highlightIndex, setHighlightIndex] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLUListElement>(null)

  const handleReloadProjects = async () => {
    if (!onReloadProjects || isReloading) return
    try {
      setIsReloading(true)
      await onReloadProjects()
    } catch (error) {
      console.error('重新載入專案失敗:', error)
    } finally {
      setIsReloading(false)
    }
  }

  const filteredProjects = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return projects
    return projects.filter((p) => p.name.toLowerCase().includes(q))
  }, [projects, search])

  // 點擊外部關閉
  useEffect(() => {
    if (!isOpen) return
    const onDocClick = (e: MouseEvent) => {
      if (!containerRef.current) return
      if (!containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
        setSearch('')
      }
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [isOpen])

  // 開啟時 focus 輸入框
  useEffect(() => {
    if (isOpen) {
      setHighlightIndex(0)
      // 等下個 frame 確保 input 已 mount
      requestAnimationFrame(() => inputRef.current?.focus())
    }
  }, [isOpen])

  // highlight 改變時自動捲動
  useEffect(() => {
    if (!isOpen || !listRef.current) return
    const el = listRef.current.querySelector<HTMLElement>(`[data-index="${highlightIndex}"]`)
    if (el) el.scrollIntoView({ block: 'nearest' })
  }, [highlightIndex, isOpen])

  const openDropdown = () => {
    if (disabled) return
    setIsOpen(true)
  }

  const handleSelect = (project: Project | null) => {
    onSelectProject(project)
    setIsOpen(false)
    setSearch('')
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlightIndex((i) => Math.min(i + 1, filteredProjects.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlightIndex((i) => Math.max(i - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const target = filteredProjects[highlightIndex]
      if (target) handleSelect(target)
    } else if (e.key === 'Escape') {
      e.preventDefault()
      setIsOpen(false)
      setSearch('')
    }
  }

  return (
    <div className={`space-y-3 ${className}`}>
      {/* 標題列 */}
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-secondary-600">
          選擇專案
        </label>

        {onReloadProjects && (
          <button
            onClick={handleReloadProjects}
            disabled={disabled || isReloading}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium
              transition-all duration-200 hover:bg-primary-50
              disabled:opacity-50 disabled:cursor-not-allowed
              text-primary-600 hover:text-primary-700"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${isReloading ? 'animate-spin' : ''}`} />
            <span>{isReloading ? '載入中...' : '重新載入'}</span>
          </button>
        )}
      </div>

      {/* 可搜尋下拉框 */}
      <div ref={containerRef} className="relative">
        {/* 觸發顯示區（未開啟時） */}
        {!isOpen && (
          <button
            type="button"
            onClick={openDropdown}
            disabled={disabled}
            className="w-full pl-12 pr-10 py-4 rounded-xl text-left
              bg-white/95 border-2 border-secondary-200/80
              text-secondary-800 font-medium
              focus:outline-none focus:border-primary-400 focus:ring-4 focus:ring-primary-100
              disabled:bg-secondary-50 disabled:text-secondary-500 disabled:cursor-not-allowed
              transition-all duration-200 relative"
          >
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <Folder className={`w-5 h-5 transition-colors duration-200
                ${selectedProject ? 'text-primary-500' : 'text-secondary-400'}`}
              />
            </div>

            {selectedProject ? (
              <span className="flex items-center gap-2">
                <span
                  className="w-3 h-3 rounded-full flex-shrink-0 ring-2 ring-white shadow-sm"
                  style={{ backgroundColor: selectedProject.color }}
                />
                <span className="truncate">{selectedProject.name}</span>
              </span>
            ) : (
              <span className="text-secondary-400">請選擇專案（可輸入文字搜尋）</span>
            )}

            <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
              <ChevronDown className="w-5 h-5 text-secondary-400" />
            </div>
          </button>
        )}

        {/* 開啟時：搜尋輸入框 */}
        {isOpen && (
          <>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <Search className="w-5 h-5 text-primary-500" />
              </div>
              <input
                ref={inputRef}
                type="text"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value)
                  setHighlightIndex(0)
                }}
                onKeyDown={handleKeyDown}
                placeholder="輸入專案名稱搜尋..."
                disabled={disabled}
                className="w-full pl-12 pr-10 py-4 rounded-xl
                  bg-white border-2 border-primary-400 ring-4 ring-primary-100
                  text-secondary-800 font-medium
                  focus:outline-none
                  disabled:bg-secondary-50 disabled:text-secondary-500
                  transition-all duration-200"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => {
                    setSearch('')
                    inputRef.current?.focus()
                  }}
                  className="absolute inset-y-0 right-0 flex items-center pr-4 text-secondary-400 hover:text-secondary-600"
                  aria-label="清除搜尋"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* 下拉選項清單 */}
            <ul
              ref={listRef}
              role="listbox"
              className="absolute z-20 mt-2 w-full max-h-72 overflow-auto
                bg-white border-2 border-secondary-200/80 rounded-xl shadow-xl
                py-1"
            >
              {/* 清除選項 */}
              {selectedProject && !search && (
                <li
                  data-index={-1}
                  onMouseDown={(e) => {
                    e.preventDefault()
                    handleSelect(null)
                  }}
                  className="px-4 py-2.5 cursor-pointer text-sm text-secondary-500
                    hover:bg-secondary-50 border-b border-secondary-100"
                >
                  清除選擇
                </li>
              )}

              {filteredProjects.length === 0 ? (
                <li className="px-4 py-6 text-center text-sm text-secondary-400">
                  找不到符合 "{search}" 的專案
                </li>
              ) : (
                filteredProjects.map((project, index) => {
                  const isHighlight = index === highlightIndex
                  const isSelected = selectedProject?.id === project.id
                  return (
                    <li
                      key={project.id}
                      data-index={index}
                      role="option"
                      aria-selected={isSelected}
                      onMouseEnter={() => setHighlightIndex(index)}
                      onMouseDown={(e) => {
                        e.preventDefault()
                        handleSelect(project)
                      }}
                      className={`px-4 py-2.5 cursor-pointer flex items-center gap-3 text-sm
                        ${isHighlight ? 'bg-primary-50' : 'hover:bg-secondary-50'}
                        ${isSelected ? 'font-semibold text-primary-700' : 'text-secondary-700'}`}
                    >
                      <span
                        className="w-3 h-3 rounded-full flex-shrink-0 ring-2 ring-white shadow-sm"
                        style={{ backgroundColor: project.color }}
                      />
                      <span className="truncate">{project.name}</span>
                    </li>
                  )
                })
              )}
            </ul>
          </>
        )}
      </div>

      {/* 最近常用專案快速選擇 */}
      {!selectedProject && !isOpen && recentFavoriteProjects.length > 0 && (
        <div className="space-y-2">
          <span className="text-xs text-secondary-500 font-medium">常用專案</span>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {recentFavoriteProjects.map((project) => (
              <button
                key={project.id}
                onClick={() => onSelectProject(project)}
                disabled={disabled}
                className="project-chip disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span
                  className="w-3 h-3 rounded-full flex-shrink-0 ring-2 ring-white shadow-sm"
                  style={{ backgroundColor: project.color }}
                />
                <span className="text-sm font-medium text-secondary-700 truncate">
                  {project.name}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* 無專案提示 */}
      {projects.length === 0 && (
        <div className="flex items-center gap-3 p-4 bg-amber-50/80 border border-amber-200/50 rounded-xl">
          <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-amber-100
            flex items-center justify-center">
            <AlertCircle className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <p className="text-sm font-medium text-amber-800">
              尚無可用專案
            </p>
            <p className="text-xs text-amber-600 mt-0.5">
              請先建立專案後再開始計時
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

export default ProjectSelector
